"use client";

import Link from "next/link";
import Image from "next/image";
import { useLenis } from "@/components/LenisProvider";
import type { FooterData } from "@/types/cms";
import {
  trackOutboundLinkClicked,
  trackCTAClicked,
  trackScrollToTopClicked,
} from "@/lib/analytics";

const DEFAULT_FOOTER_DATA: FooterData = {
  id: 0,
  documentId: "",
  createdAt: "",
  updatedAt: "",
  publishedAt: null,
  locale: null,
  brandSection: {
    id: 0,
    description:
      "The Enterprise VIP Network for curated gatherings where the right people exchange insights and make new connections.",
    logoLink: null,
    logoImage: null,
  },
  linkColumns: [
    {
      id: 1,
      title: "Business",
      links: [
        { id: 1, label: "For Sponsors", url: "https://sponsor.country.club/" },
        { id: 2, label: "For Hosts", url: "https://dinner.country.club/" },
        {
          id: 3,
          label: "For Conferences",
          url: "https://conference.country.club/",
        },
      ],
    },
    {
      id: 2,
      title: "Information",
      links: [
        { id: 4, label: "About Us", url: "https://country.club/about" },
        { id: 5, label: "Contact Us", url: "https://country.club/contact" },
        { id: 6, label: "Join Our Team", url: "https://country.club/careers" },
        {
          id: 7,
          label: "Opportunity",
          url: "https://country.club/opportunity",
        },
      ],
    },
    {
      id: 3,
      title: "Legal",
      links: [
        {
          id: 8,
          label: "Terms & Conditions",
          url: "https://country.club/terms",
        },
        { id: 9, label: "Privacy Policy", url: "https://country.club/privacy" },
      ],
    },
  ],
  careersCta: {
    id: 0,
    title: "Join Our Team",
    description:
      "We're building the future of professional networking. See open roles.",
    buttonText: "View Careers",
    buttonLink: "https://country.club/careers",
  },
  bottomBar: {
    id: 0,
    copyrightText: "Country Club. All rights reserved.",
    bottomLinks: [
      { id: 1, label: "Privacy", url: "https://country.club/privacy" },
      { id: 2, label: "Terms", url: "https://country.club/terms" },
    ],
  },
};

interface FooterProps {
  data?: FooterData;
}

export default function Footer({ data = DEFAULT_FOOTER_DATA }: FooterProps) {
  const currentYear = new Date().getFullYear();
  const lenis = useLenis();

  const scrollToTop = () => {
    trackScrollToTopClicked();
    if (lenis) {
      lenis.scrollTo(0, {
        duration: 1.5,
        offset: 0,
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const logoLink = data.brandSection?.logoLink || "https://country.club/";
  const logoImage = data.brandSection?.logoImage?.url || "/branding/logo.webp";
  const brandDescription = data.brandSection?.description || "";

  return (
    <footer className="relative z-10 bg-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 mb-12">
          {/* Brand Column */}
          <div className="space-y-4">
            {/* Logo */}
            <Link href={logoLink} className="flex items-center gap-2">
              <Image
                src={logoImage}
                alt="Country Club"
                width={120}
                height={40}
                className="h-8 sm:h-10 w-auto"
              />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed">
              {brandDescription}
            </p>
          </div>

          {/* Dynamic Link Columns */}
          {data.linkColumns.map((column) => (
            <div key={column.id} className="space-y-4">
              <h4 className="text-white font-medium">{column.title}</h4>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.id}>
                    <Link
                      href={link.url}
                      onClick={() =>
                        trackOutboundLinkClicked(
                          link.url,
                          link.label,
                          `footer_${column.title.toLowerCase()}`,
                        )
                      }
                      className="text-white/60 hover:text-[#D4A84B] transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Careers CTA */}
        {data.careersCta && (
          <>
            <div className="border-t border-white/10 my-8" />
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6 px-6 sm:px-8 rounded-2xl bg-white/3 border border-white/10">
              <div>
                <h4 className="text-white font-medium text-base mb-1">
                  {data.careersCta.title}
                </h4>
                <p className="text-white/50 text-sm">
                  {data.careersCta.description}
                </p>
              </div>
              <Link
                href={data.careersCta.buttonLink}
                onClick={() =>
                  trackCTAClicked({
                    cta_location: "footer",
                    cta_type: "career",
                    cta_text: data.careersCta!.buttonText,
                    link_url: data.careersCta!.buttonLink,
                  })
                }
                className="shrink-0 px-6 py-2.5 bg-[#D4A84B] text-black text-sm font-semibold rounded-xl hover:bg-[#c49a3f] transition-all hover:scale-105"
              >
                {data.careersCta.buttonText}
              </Link>
            </div>
          </>
        )}

        {/* Divider */}
        <div className="border-t border-white/10 my-8" />

        {/* Copyright */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-sm">
            &copy; {currentYear}{" "}
            {data.bottomBar?.copyrightText ||
              "Country Club. All rights reserved."}
          </p>
          <div className="flex items-center gap-6">
            {data.bottomBar?.bottomLinks?.map((link) => (
              <Link
                key={link.id}
                href={link.url}
                onClick={() =>
                  trackOutboundLinkClicked(
                    link.url,
                    link.label,
                    "footer_bottom",
                  )
                }
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
      <button
        onClick={scrollToTop}
        className="absolute bottom-8 right-8 p-3 rounded-full bg-white/5 hover:bg-[#D4A84B]/20 border border-white/10 hover:border-[#D4A84B]/50 text-white/60 hover:text-[#D4A84B] transition-all duration-300 backdrop-blur-sm z-50 shadow-lg"
        aria-label="Scroll to top"
      >
        <svg
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </footer>
  );
}
